import React from "react";
import { motion } from "framer-motion";
import { useLang } from "../i18n/LanguageContext";

const ease = [0.16, 1, 0.3, 1];

const RESEARCH_ITEMS = [
  {
    url: "/research/ombrieres/mairie-drussenheim.webp",
    title: {
      fr: "Maison du Pays Rhénan — Ombrière PV · Drussenheim",
      en: "Maison du Pays Rhénan — PV shade · Drussenheim",
    },
    code: "R·01",
    kind: "Étude de faisabilité",
    aspect: "wide",
  },
  {
    url: "/research/ombrieres/piscine-odonates.webp",
    title: {
      fr: "Piscine Odonates — Ombrière PV · Drussenheim",
      en: "Odonates Pool — PV shade · Drussenheim",
    },
    code: "R·02",
    kind: "Étude de faisabilité",
    aspect: "wide",
  },
  {
    url:
      "https://customer-assets.emergentagent.com/job_laka-design-refresh/artifacts/iokk2f8x_Nantes.jpg",
    title: { fr: "Nantes — Pont métallique", en: "Nantes — Steel bridge" },
    code: "R·03",
  },
  {
    url:
      "https://customer-assets.emergentagent.com/job_laka-design-refresh/artifacts/vd0pggix_renudu%2Bshwighousse.jpg",
    title: { fr: "Le Forest — Schweighouse-sur-Moder", en: "Le Forest — Schweighouse" },
    code: "R·04",
  },
  {
    url:
      "https://customer-assets.emergentagent.com/job_laka-design-refresh/artifacts/79cook86_Residence.jpg",
    title: { fr: "Résidence collective — Étude", en: "Collective residence — Study" },
    code: "R·05",
  },
  {
    url:
      "https://customer-assets.emergentagent.com/job_laka-design-refresh/artifacts/rsce3n2w_logements.jpg",
    title: { fr: "Logements — Façade urbaine", en: "Housing — Urban facade" },
    code: "R·06",
  },
  {
    url:
      "https://customer-assets.emergentagent.com/job_laka-design-refresh/artifacts/58xxti5m_Maison.jpeg",
    title: {
      fr: "Maison méditerranéenne — Variations",
      en: "Mediterranean house — Variations",
    },
    code: "R·07",
  },
  {
    url:
      "https://customer-assets.emergentagent.com/job_laka-design-refresh/artifacts/88v4p9zs_Plan%20Texture%26MATERIAUX.jpeg",
    title: { fr: "Plan masse — Textures & matériaux", en: "Site plan — Textures & materials" },
    code: "R·08",
  },
];

export default function Research() {
  const { lang } = useLang();

  return (
    <section
      id="research"
      data-testid="research-section"
      className="py-24 md:py-36 bg-[#F7F7F9] border-t border-[#E5E5E5]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="w-8 h-px bg-[#0A0A0A]" />
          <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase">
            {lang === "fr" ? "Travaux non répertoriés" : "Unfiled works"} / 04
          </span>
        </motion.div>

        <div className="grid grid-cols-12 gap-8 mb-14 md:mb-20 items-end">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease }}
            className="col-span-12 md:col-span-8 font-display font-light uppercase tracking-[-0.02em] leading-[0.95] text-[clamp(2.25rem,5.5vw,5.5rem)] max-w-[22ch]"
          >
            {lang === "fr"
              ? "Travaux & recherches."
              : "Works & research."}
            <span className="text-[#D35400]">_</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease, delay: 0.15 }}
            className="col-span-12 md:col-span-4 text-base md:text-lg font-light text-[#5C5C5C] max-w-[52ch]"
          >
            {lang === "fr"
              ? "Rendus, études, concours, esquisses, plans et visualisations 3D — un atelier de références continuellement en mouvement."
              : "Renders, studies, competitions, sketches, plans and 3D visualisations — a continuously evolving reference workshop."}
          </motion.p>
        </div>

        {/* Grid of research items */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {RESEARCH_ITEMS.map((item, i) => {
            const isWide = item.aspect === "wide";
            const spanCls = isWide ? "sm:col-span-2 lg:col-span-3" : "";
            const aspectCls = isWide ? "aspect-[21/8]" : "aspect-[4/3]";
            const objectCls = isWide ? "object-contain bg-white p-2 md:p-4" : "object-cover group-hover:scale-[1.04]";
            return (
              <motion.figure
                key={item.code}
                data-testid={`research-${item.code}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, ease, delay: (i % 3) * 0.08 }}
                className={`group ${spanCls}`}
              >
                <div className={`relative overflow-hidden bg-[#EFEFEF] ${aspectCls} border border-[#E5E5E5]`}>
                  <img
                    src={item.url}
                    alt={item.title[lang]}
                    loading="lazy"
                    className={`laka-photo w-full h-full ${objectCls}`}
                  />
                  <div className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[#0A0A0A] bg-white/85 px-2 py-1">
                    {item.code}
                  </div>
                </div>
                <figcaption className="mt-3 flex items-baseline justify-between gap-4">
                  <span className="font-display text-base md:text-lg tracking-tight text-[#0A0A0A]">
                    {item.title[lang]}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#D35400]">
                    {item.kind || (lang === "fr" ? "Étude" : "Study")}
                  </span>
                </figcaption>
              </motion.figure>
            );
          })}
        </div>
      </div>
    </section>
  );
}
